/**
 * EmailList — TASK-808.
 *
 * The correspondence Kielis has already indexed, as a canvas tile. The inbound counterpart to
 * `EmailDraft`: that one is what you are sending, this one is what arrived.
 *
 * **Not a thin adapter.** The convention in `widgets/index.tsx` is "wrap an existing
 * self-fetching Kielis component — reuse, don't rebuild", and there was no email-list component
 * anywhere in `frontend/src` to wrap. So this genuinely builds one, in its own file like
 * `ClientBook` and `MeetingsList` rather than pretending to be a wrapper.
 *
 * **DEC-1 — every fetch that puts correspondence on the advisor's screen writes an audit row.**
 * The trail's promise is "this is every time an RM was shown a client's correspondence", and it
 * is kept without exceptions to reason about later. The price is paid here, in behaviour:
 *
 *   - No polling, no background refetch, and no refetch on remount. A tile that fetched on a
 *     timer would write audit rows for correspondence nobody looked at, destroying the very
 *     trail the decision protects. Every refetch option — `staleTime` included, since it is
 *     what decides whether a remount counts as stale — is set EXPLICITLY below rather than
 *     inherited from `queryClient`, because a global default is one edit away from being
 *     flipped for everyone.
 *   - No body prefetch. A row's body is fetched when the advisor opens it, never before —
 *     `EMAIL_MESSAGE_READ` must mean the advisor actually read it.
 *   - The counterweight: the tile says when it last fetched and offers a refresh, so "no
 *     polling" does not quietly hand the advisor a stale mailbox that looks live.
 *
 * **Three honest empty states.** Not connected / connected but never synced / connected and
 * synced with nothing matching. An empty list that reads as "you have no email" is the failure
 * this widget exists to stop repeating, so connection state is read from
 * `emailConnectionService.getStatus()` and never inferred from a zero-length result.
 */

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import DOMPurify from 'dompurify';
import { AlertCircle, ChevronDown, ChevronRight, Mail, Reply, RefreshCw } from 'lucide-react';

import { WidgetContainer } from '../WidgetContainer';
import { useCanvasActions } from '../../../stores/canvasStore';
import { listEmails, getEmailBody, type EmailCandidate } from '../../../services/api/emailApi';
import { emailConnectionService } from '../../../services/emailConnectionService';
import type { EmailProviderStatus } from '../../../types/emailConnection.types';
import { cn } from '../../../lib/utils';

interface EmailListProps {
  /**
   * Narrow to ONE client's correspondence. Optional by design — with no `clientId` this is the
   * advisor's own mailbox, and "show my emails" must never demand a client (the widget is
   * `_CLIENT_OPTIONAL`, TASK-707). Server-injected from the resolved focus; the model never
   * emits an id, and never an address.
   */
  clientId?: string;
  clientName?: string;
  title?: string;
}

/**
 * Never fetch unless the advisor acted. DEC-1: an unattended tile must not write audit rows.
 *
 * `staleTime: Infinity` is load-bearing and belongs HERE rather than in `queryClient`. The app
 * default is five minutes, and `refetchOnMount` only fires for data considered stale — so a
 * tile collapsed before a meeting and expanded after one would refetch on remount and write an
 * `EMAIL_DISCOVER` row nobody asked for. `CanvasTile` unmounts children on collapse and the
 * canvas remounts a tile when it is pinned to the rail or the thread changes; none of those is
 * the advisor asking to read mail.
 */
const NO_BACKGROUND_FETCHING = {
  refetchInterval: false as const,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
  refetchOnMount: false,
  staleTime: Infinity,
};

/**
 * A vault token that could not be resolved (`reidentify_from_vault` returns text unchanged on
 * a DB error, and a token whose vault write failed has no row). Showing the advisor
 * `[PERSON~5ab4f0f0]` is worse than saying the name is unavailable — it looks like corruption
 * and tells them nothing.
 */
const VAULT_TOKEN = /\[[A-Z][A-Z_]*~[0-9a-f]{8}\]/g;

export function readable(value: string | null, fallback: string): string {
  if (!value) return fallback;
  const cleaned = value.replace(VAULT_TOKEN, '…').trim();
  return cleaned === '…' || cleaned === '' ? fallback : cleaned;
}

/**
 * An email body rendered as the sender wrote it — TASK-815.
 *
 * Mailbox HTML is UNTRUSTED third-party content (TASK-622/623 fence it for exactly this
 * reason), so it gets two independent layers, not one:
 *
 *  1. **DOMPurify** strips scripts, event handlers, `<iframe>`, `<object>` and javascript:
 *     URLs before the markup is ever handed to the browser.
 *  2. A **sandboxed iframe** with `sandbox` set to the empty string — no scripts, no forms,
 *     no plugins, no top-level navigation, and crucially no same-origin, so even markup that
 *     survived step 1 cannot read this document, its cookies or its localStorage.
 *
 * Remote images are left in place deliberately (`ADD_ATTR: target`), because a mail client
 * that silently strips them shows a broken message. They are a tracking-pixel vector; the
 * iframe's `referrerpolicy` keeps the advisor's URL out of the request. Blocking them behind
 * a "load images" affordance is the obvious next step if that trade is judged wrong.
 */
function HtmlBody({ html }: { html: string }) {
  const safe = useMemo(
    () =>
      DOMPurify.sanitize(html, {
        FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'input', 'style', 'base'],
        FORBID_ATTR: ['onerror', 'onload', 'onclick', 'srcdoc', 'formaction'],
        ADD_ATTR: ['target'],
      }),
    [html],
  );

  return (
    <iframe
      // sandbox="" is the STRICTEST value, not a missing one: every capability is
      // withheld, including same-origin. Do not "fix" this by adding allow-* tokens.
      sandbox=""
      referrerPolicy="no-referrer"
      title="Email message"
      className="h-[320px] w-full rounded border border-border/60 bg-white"
      srcDoc={`<!doctype html><meta charset="utf-8">
<style>body{font:13px/1.5 system-ui,sans-serif;color:#111;margin:8px;word-break:break-word}
img{max-width:100%;height:auto}a{color:#1d4ed8}table{max-width:100%}</style>${safe}`}
    />
  );
}

type MailboxState =
  | { kind: 'loading' }
  | { kind: 'unavailable' }
  | { kind: 'disconnected' }
  | { kind: 'never-synced'; connectedAt: string | null }
  | { kind: 'ready' };

/**
 * What the connection status means for this tile. Kept separate from rendering so the
 * three-empty-states rule is one readable decision rather than nested JSX conditions.
 */
export function mailboxStateFrom(
  providers: EmailProviderStatus[] | undefined,
  isLoading: boolean,
  failed: boolean,
): MailboxState {
  if (isLoading) return { kind: 'loading' };
  // A status we could not read is NOT a disconnected mailbox. Saying "connect one" to an
  // advisor whose mailbox works is the same false claim this widget exists to stop.
  if (failed || !providers) return { kind: 'unavailable' };
  const connected = providers.filter((p) => p.connected);
  if (connected.length === 0) return { kind: 'disconnected' };
  // `lastSync === null` means genuinely never synced — TASK-811 made that distinguishable.
  // Before it, this field carried the connection time and every mailbox looked synced.
  if (connected.every((p) => p.lastSync === null)) {
    return { kind: 'never-synced', connectedAt: connected[0].connectedAt };
  }
  return { kind: 'ready' };
}

function EmailRow({ email, clientId, clientName }: {
  email: EmailCandidate;
  clientId?: string;
  clientName?: string;
}) {
  const [open, setOpen] = useState(false);
  const [asText, setAsText] = useState(false);
  const { addSpecs } = useCanvasActions();
  // `enabled: open` is the whole no-prefetch rule: the body request is not made until the
  // advisor opens the row, so an `EMAIL_MESSAGE_READ` row exists only for mail actually read.
  const body = useQuery({
    queryKey: ['email-body', email.email_id],
    queryFn: () => getEmailBody(email.email_id),
    enabled: open,
    ...NO_BACKGROUND_FETCHING,
  });

  return (
    <li className="border-b border-border/60 last:border-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-start gap-2 px-1 py-2 text-left hover:bg-accent/40"
      >
        {open ? (
          <ChevronDown className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        )}
        <span className="min-w-0 flex-1">
          <span className="flex items-baseline justify-between gap-2">
            <span className="truncate text-sm font-medium">
              {readable(email.subject, '(No subject)')}
            </span>
            {email.date && (
              <span className="shrink-0 text-[11px] text-muted-foreground">
                {format(new Date(email.date), 'd MMM yyyy')}
              </span>
            )}
          </span>
          <span className="truncate block text-xs text-muted-foreground">
            {readable(email.sender, 'Unknown sender')}
          </span>
          {!open && email.snippet && (
            <span className="mt-0.5 line-clamp-1 block text-xs text-muted-foreground/80">
              {readable(email.snippet, '')}
            </span>
          )}
        </span>
      </button>

      {open && (
        <div className="px-1 pb-3 pl-7 text-xs">
          {body.isLoading && <span className="text-muted-foreground">Fetching message…</span>}
          {body.isError && (
            <span className="text-amber-600">
              Could not fetch this message from the mail provider.
            </span>
          )}
          {body.data && (
            <>
              <div className="mb-1.5 flex items-center gap-3">
                {/* TASK-815: Reply hands off to EmailDraft rather than growing a composer
                    here — that widget already owns the grounded draft → approve → send spine
                    (TASK-624/625), including the compliance locks and the hard-gated send.
                    It needs a client, so it is offered only when this mail belongs to one;
                    a disabled button with a reason beats one that fails on click. */}
                <button
                  type="button"
                  disabled={!clientId}
                  title={
                    clientId
                      ? `Draft a reply to ${clientName ?? 'this client'}`
                      : 'Replying needs a client — this sender is not in your book'
                  }
                  onClick={() =>
                    clientId &&
                    addSpecs([{ component: 'EmailDraft', props: { clientId } }])
                  }
                  className={cn(
                    'flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px]',
                    clientId
                      ? 'text-kielis-blue hover:bg-accent'
                      : 'cursor-not-allowed text-muted-foreground/50',
                  )}
                >
                  <Reply className="h-3 w-3" />
                  Reply
                </button>

                {body.data.body_html && (
                  <button
                    type="button"
                    onClick={() => setAsText((v) => !v)}
                    className="text-[11px] text-muted-foreground hover:text-foreground"
                  >
                    {asText ? 'Show formatted' : 'Show plain text'}
                  </button>
                )}
              </div>

              {body.data.body_html && !asText ? (
                <HtmlBody html={body.data.body_html} />
              ) : (
                <pre className="whitespace-pre-wrap break-words font-sans text-foreground/90">
                  {body.data.body_text || '(This message has no text body.)'}
                </pre>
              )}
            </>
          )}
        </div>
      )}
    </li>
  );
}

export function EmailList({ clientId, clientName, title }: EmailListProps) {
  const status = useQuery({
    queryKey: ['email-connection-status'],
    queryFn: () => emailConnectionService.getStatus(),
    ...NO_BACKGROUND_FETCHING,
    refetchOnMount: true,
  });

  const mailbox = useMemo(
    () =>
      mailboxStateFrom(
        status.data ? [status.data.gmail, status.data.msgraph] : undefined,
        status.isLoading,
        status.isError,
      ),
    [status.data, status.isLoading, status.isError],
  );

  const emails = useQuery({
    queryKey: ['email-list', clientId ?? 'me'],
    queryFn: () => listEmails({ clientId }),
    // Only once a mailbox is known to exist: fetching against a disconnected mailbox would
    // write an audit row for a read that could not return anything.
    enabled: mailbox.kind === 'ready',
    ...NO_BACKGROUND_FETCHING,
  });

  const heading = title ?? (clientName ? `${clientName} — email` : 'My mailbox');
  const rows = emails.data?.emails ?? [];

  return (
    <WidgetContainer
      title={heading}
      source="Mailbox (indexed)"
      badges={
        mailbox.kind === 'ready' && (
          <button
            type="button"
            onClick={() => emails.refetch()}
            disabled={emails.isFetching}
            className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
          >
            <RefreshCw className={cn('h-3 w-3', emails.isFetching && 'animate-spin')} />
            {/* DEC-1's counterweight: no polling means the advisor must be told how old this
                is, or a silently-stale mailbox looks live. */}
            {/* From the query cache, not component state: a remount renders cached rows
                without refetching, and a tile that then showed no timestamp would be exactly
                the silently-stale mailbox DEC-1's refresh rule exists to prevent. */}
            {emails.dataUpdatedAt
              ? `Updated ${format(new Date(emails.dataUpdatedAt), 'HH:mm')}`
              : 'Refresh'}
          </button>
        )
      }
    >
      {mailbox.kind === 'loading' && (
        <p className="text-sm text-muted-foreground">Checking your mailbox connection…</p>
      )}

      {mailbox.kind === 'unavailable' && (
        <EmptyState
          icon={<AlertCircle className="h-4 w-4" />}
          title="Can't check your mailbox connection right now"
          body="This is a problem reaching the connection service, not a statement about your mailbox. Try refreshing shortly."
        />
      )}

      {mailbox.kind === 'disconnected' && (
        <EmptyState
          icon={<Mail className="h-4 w-4" />}
          title="No mailbox connected"
          body="Kielis can read your Gmail or Microsoft 365 mailbox once you connect it."
          action={
            <a href="/settings" className="text-kielis-blue hover:underline">
              Connect a mailbox in Settings →
            </a>
          }
        />
      )}

      {mailbox.kind === 'never-synced' && (
        <EmptyState
          icon={<Mail className="h-4 w-4" />}
          title="Mailbox connected, but not synced yet"
          body={
            mailbox.connectedAt
              ? `Connected on ${format(new Date(mailbox.connectedAt), 'd MMM yyyy')}. Nothing has been indexed yet, so there is nothing to show — this is not an empty mailbox.`
              : 'Nothing has been indexed yet, so there is nothing to show — this is not an empty mailbox.'
          }
          action={
            <a href="/settings" className="text-kielis-blue hover:underline">
              Run a sync from Settings →
            </a>
          }
        />
      )}

      {mailbox.kind === 'ready' && (
        <>
          {emails.isLoading && <p className="text-sm text-muted-foreground">Loading messages…</p>}
          {emails.isError && (
            <EmptyState
              icon={<AlertCircle className="h-4 w-4" />}
              title="Couldn't load your messages"
              body="Your mailbox is connected — this is a problem reading the index, not an empty inbox."
            />
          )}
          {emails.data?.unresolved_client && (
            <EmptyState
              icon={<AlertCircle className="h-4 w-4" />}
              title={`Couldn't identify ${clientName ?? 'that client'}`}
              body="Rather than show you everyone's correspondence, nothing is shown. Try naming the client again."
            />
          )}
          {emails.data && !emails.data.unresolved_client && rows.length === 0 && (
            <EmptyState
              icon={<Mail className="h-4 w-4" />}
              title="Nothing matched"
              body={
                clientName
                  ? `Your mailbox is synced, and nothing from ${clientName} is indexed.`
                  : 'Your mailbox is synced. Nothing in the indexed correspondence matches.'
              }
            />
          )}
          {rows.length > 0 && (
            <ul className="-mx-1">
              {rows.map((email) => (
                <EmailRow
                  key={email.email_id}
                  email={email}
                  clientId={email.client_id ?? clientId}
                  clientName={email.client_name ?? clientName}
                />
              ))}
            </ul>
          )}
        </>
      )}
    </WidgetContainer>
  );
}

function EmptyState({
  icon,
  title,
  body,
  action,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col items-start gap-1 py-2 text-sm">
      <span className="flex items-center gap-1.5 font-medium text-foreground">
        {icon}
        {title}
      </span>
      <p className="text-xs text-muted-foreground">{body}</p>
      {action && <div className="mt-1 text-xs">{action}</div>}
    </div>
  );
}

export default EmailList;
